import cn from "classnames"
import React, { useCallback, useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ClientRole } from "@/api/graphql"
import { type FormInputs } from "@/common/components/form_input"
import env from "@/common/env"
import InfoFill from "@/skylticons/info_fill"
import "../styles/client_role_switch.css"

const Radio: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = props => {
  if (!props.name) {
    throw new Error("Name is required")
  }

  const { register } = useFormContext<FormInputs>()
  const registerFields = register(props.name)

  return (
    <>
      <input
        type="radio"
        {...registerFields}
        {...props}
        onChange={e => {
          registerFields.onChange(e)

          if (props.onChange) props.onChange(e)
        }}
        className={cn(props.className, "testenv-radio", { hidden: !env.TEST, active: props.checked })}
      />
      <span className="new-checkbox"></span>
    </>
  )
}

interface ClientRoleSwitchProps {
  clientRole?: ClientRole
  setClientRole?: (role: ClientRole) => void
  error?: boolean
  disabled?: boolean
}

const ClientRoleSwitch: React.FC<ClientRoleSwitchProps> = ({ clientRole, setClientRole, error, disabled }) => {
  const { t } = useTranslation()
  const switchRef = useRef<HTMLDivElement>(null)
  const consumerRef = useRef<HTMLDivElement>(null)
  const businessRef = useRef<HTMLDivElement>(null)
  const consumer = clientRole == ClientRole.consumer
  const business = clientRole == ClientRole.business

  const { setValue, clearErrors } = useFormContext<FormInputs>()

  const setRole = useCallback(
    (role: ClientRole) => {
      setClientRole && setClientRole(role)
      setValue("clientRole", role)
      clearErrors("clientRole")
    },
    [setClientRole, setValue, clearErrors]
  )

  useEffect(() => {
    if (error && switchRef.current) {
      switchRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [error])

  const toggleRole = useCallback(() => {
    if (business) {
      setRole(ClientRole.consumer)
      consumerRef.current?.focus()
    } else {
      setRole(ClientRole.business)
      businessRef.current?.focus()
    }
  }, [business, setRole])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.currentTarget
      let handled = false

      if (e.key == " ") {
        if (target == consumerRef.current) {
          setRole(ClientRole.consumer)
        } else {
          setRole(ClientRole.business)
        }

        handled = true
      }

      if (["Up", "ArrowUp", "Left", "ArrowLeft", "Down", "ArrowDown", "Right", "ArrowRight"].includes(e.key)) {
        toggleRole()
        handled = true
      }

      if (handled) {
        e.stopPropagation()
        e.preventDefault()
      }
    },
    [setRole, toggleRole]
  )

  return (
    <>
      <div
        className={cn("form-role-switch mt-3 flex justify-between", {
          "rounded border-1 border-error": error,
          "mb-3": !error,
          "pointer-events-none opacity-50": disabled,
        })}
        ref={switchRef}
      >
        <div className="radio-group flex flex-wrap" role="radiogroup">
          <div
            role="radio"
            className="radio consumer m-0 p-3"
            aria-checked={consumer}
            tabIndex={business ? -1 : 0}
            onKeyDown={handleKeyDown}
            ref={consumerRef}
          >
            <label className="!mb-0 whitespace-nowrap">
              <Radio
                name="clientRole"
                value={ClientRole.consumer}
                onChange={() => setRole(ClientRole.consumer)}
                checked={consumer}
              />
              <span className="title paragraph-sm bold">{t("customer.consumer")}</span>
            </label>
          </div>

          <div
            role="radio"
            className="radio business m-0 p-3"
            aria-checked={business}
            tabIndex={business ? 0 : -1}
            onKeyDown={handleKeyDown}
            ref={businessRef}
          >
            <label className="!mb-0 whitespace-nowrap">
              <Radio
                name="clientRole"
                value={ClientRole.business}
                onChange={() => setRole(ClientRole.business)}
                checked={business}
              />
              <span className="title paragraph-sm bold">{t("customer.business")}</span>
            </label>
          </div>
        </div>

        {error && <InfoFill className="mx-3 my-auto h-6 min-h-[24px] w-6 min-w-[24px] text-error" />}
      </div>

      {error && <div className="mb-3 mt-1 text-error">{t("address.required_information")}</div>}
    </>
  )
}

export default ClientRoleSwitch
